'use client';

import * as React from 'react';
import { cn } from '../utils/cn';

type ChatLayoutProps = {
  children: React.ReactNode;
  className?: string;
};

type ChatSidebarProps = {
  children: React.ReactNode;
  className?: string;
};

type ChatContentProps = {
  children?: React.ReactNode;
  className?: string;
};

type ChatThreadItemProps = {
  children: React.ReactNode;
  isActive?: boolean;
  onClick?: () => void;
  className?: string;
  title?: string;
};

const ChatLayout = React.forwardRef<HTMLDivElement, ChatLayoutProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex h-full min-h-0 bg-background',
        // Inset padding for nested container effect (Apple style)
        'p-3 gap-3',
        className
      )}
      data-testid="chat-layout"
      {...props}
    >
      {children}
    </div>
  )
);
ChatLayout.displayName = 'ChatLayout';

const ChatSidebar = React.forwardRef<HTMLDivElement, ChatSidebarProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // Inset nested container (Apple Ventura style)
        // Rounded card that floats inside parent with padding
        // Responsive width: narrower on small screens, wider on large
        // Apple-style pop animation on mount
        'w-40 sm:w-48 xl:w-64 h-full glass-card-bg glass-card-border rounded-2xl p-3 flex-shrink-0 overflow-y-auto sidebar-animate-in',
        className
      )}
      data-testid="chat-sidebar"
      {...props}
    >
      {children}
    </div>
  )
);
ChatSidebar.displayName = 'ChatSidebar';

const ChatContent = React.forwardRef<HTMLDivElement, ChatContentProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // Inset nested container for content area
        'flex-1 min-w-0 min-h-0 h-full glass-card-bg glass-card-border rounded-2xl overflow-hidden',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
);
ChatContent.displayName = 'ChatContent';

const ChatThreadItem = React.forwardRef<HTMLButtonElement, ChatThreadItemProps>(
  ({ className, children, isActive, onClick, title, ...props }, ref) => (
    <button
      ref={ref}
      onClick={onClick}
      title={title}
      className={cn(
        'w-full text-left px-3 py-2.5 text-sm transition-colors duration-150 ease-out',
        'cursor-pointer text-foreground rounded-lg',
        isActive ? 'bg-primary/15 hover:bg-primary/20' : 'hover:bg-muted/60',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-1',
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
);
ChatThreadItem.displayName = 'ChatThreadItem';

export { ChatContent, ChatLayout, ChatSidebar, ChatThreadItem };
